import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → SMS page.
 *
 * A status + test harness for Lyric's SMS tool (server/routes/sms.js). It shows
 * whether the shared provider connection (Twilio today) is configured, lets an
 * admin send a one-off test message, and surfaces the inbound-reply webhook URL
 * to paste into the provider console. All provider calls are brokered
 * server-side — no messaging credentials ever reach the browser.
 *
 * Credentials live in AWS Secrets Manager (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN,
 * and a Messaging Service SID or From number) — see docs/sms-setup.md.
 */

const E164 = /^\+[1-9]\d{6,14}$/;

/** Small status line under a reply showing the Salesforce write-back outcome. */
function WorkflowBadge({ wf }) {
    if (!wf) return null;
    // CTIA keyword replies (JOIN/STOP/HELP) aren't Opportunity matches — show
    // the consent action + auto-reply outcome instead of "No matching Opportunity".
    if (wf.handledBy === 'keyword') {
        const label = wf.action === 'opt_in' ? 'Opt-in recorded (JOIN/START)'
            : wf.action === 'opt_out' ? 'Opt-out recorded (STOP)'
                : 'Help reply';
        const r = wf.reply || {};
        const replyState = r.sent ? `auto-reply sent${r.sid ? ` · ${r.sid}` : ''}`
            : r.skipped ? `auto-reply skipped (${r.reason || 'provider not configured'})`
                : r.error ? `auto-reply failed: ${r.error}`
                    : 'no auto-reply';
        return <div style={styles.wfOk}>✓ {label} — {replyState}</div>;
    }
    if (wf.appended) {
        return (
            <div style={styles.wfOk}>
                ✓ Appended to {wf.object || 'Opportunity'} <code>{wf.opportunityId}</code>
                {wf.field ? <> · <code>{wf.field}</code></> : null}
            </div>
        );
    }
    if (wf.matched && wf.reason === 'salesforce_not_configured') {
        return <div style={styles.wfWarn}>↪ Matched {wf.opportunityId ? <code>{wf.opportunityId}</code> : 'an Opportunity'} — Salesforce not configured, write-back skipped</div>;
    }
    if (wf.matched && wf.error) {
        return <div style={styles.wfWarn}>⚠ Matched <code>{wf.opportunityId}</code> but write failed: {wf.error}</div>;
    }
    if (!wf.matched) {
        return <div style={styles.wfMuted}>No matching Opportunity (sender not in an open conversation)</div>;
    }
    return null;
}

export default function AdminSms({ embedded = false }) {
    const { oktaAuth } = useOktaAuth() || {};

    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const [statusError, setStatusError] = useState('');

    const [to, setTo] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [sendError, setSendError] = useState('');

    const [recent, setRecent] = useState([]);
    const [recentError, setRecentError] = useState('');
    const [simFrom, setSimFrom] = useState('+15559876543');
    const [simBody, setSimBody] = useState('');
    const [simOpp, setSimOpp] = useState('');
    const [simulating, setSimulating] = useState(false);
    const [simError, setSimError] = useState('');

    // Salesforce "stale Lead Notes" workflow
    const [sfSettings, setSfSettings] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [scanDays, setScanDays] = useState(14);
    const [scanning, setScanning] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [wfError, setWfError] = useState('');
    const [wfNotice, setWfNotice] = useState('');
    const [notifyResults, setNotifyResults] = useState([]);

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const loadStatus = useCallback(async () => {
        setLoadingStatus(true);
        setStatusError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/sms/status', { headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setStatus(data);
        } catch (e) {
            setStatusError(e.message);
            setStatus(null);
        } finally {
            setLoadingStatus(false);
        }
    }, [authHeader]);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const toValid = E164.test(to.trim());
    const canSend = toValid && body.trim().length > 0 && !sending;

    const sendTest = useCallback(async () => {
        setSending(true);
        setSendResult(null);
        setSendError('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/sms/send', {
                method: 'POST',
                headers,
                body: JSON.stringify({ to: to.trim(), body }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setSendResult(data);
        } catch (e) {
            setSendError(e.message);
        } finally {
            setSending(false);
        }
    }, [authHeader, to, body]);

    // Prefer the server's configured public base URL (stable behind the ALB/proxy
    // and what Twilio signature verification pins to); fall back to the browser
    // origin only when it isn't set.
    const webhookBase = (status && status.publicBaseUrl)
        ? status.publicBaseUrl
        : (typeof window !== 'undefined' ? window.location.origin : '');
    const webhookUrl = `${webhookBase}/api/sms/inbound`;

    const loadRecent = useCallback(async () => {
        setRecentError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/sms/inbound/recent', { headers });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setRecent(Array.isArray(data.messages) ? data.messages : []);
        } catch (e) {
            setRecentError(e.message);
        }
    }, [authHeader]);

    // Poll the recent-replies feed so simulated and real inbound replies both
    // show up live during a demo without a manual refresh.
    useEffect(() => {
        loadRecent();
        const id = setInterval(loadRecent, 5000);
        return () => clearInterval(id);
    }, [loadRecent]);

    const simValid = E164.test(simFrom.trim()) && simBody.trim().length > 0 && !simulating;

    const simulateInbound = useCallback(async () => {
        setSimulating(true);
        setSimError('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/sms/simulate-inbound', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    from: simFrom.trim(),
                    body: simBody,
                    opportunityId: simOpp.trim() || undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setSimBody('');
            await loadRecent();
        } catch (e) {
            setSimError(e.message);
        } finally {
            setSimulating(false);
        }
    }, [authHeader, simFrom, simBody, simOpp, loadRecent]);

    const loadSfSettings = useCallback(async () => {
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/sms/leadnotes/settings', { headers });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.settings) setSfSettings(data.settings);
        } catch { /* workflow is optional */ }
    }, [authHeader]);

    useEffect(() => { loadSfSettings(); }, [loadSfSettings]);

    const scanStale = useCallback(async () => {
        setScanning(true);
        setWfError('');
        setWfNotice('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/sms/leadnotes/scan', {
                method: 'POST',
                headers,
                body: JSON.stringify({ days: Number(scanDays) || undefined }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setCandidates(Array.isArray(data.candidates) ? data.candidates : []);
            if ((data.candidates || []).length === 0) setWfNotice('No stale Opportunities with an owner phone were found.');
        } catch (e) {
            setWfError(e.message);
        } finally {
            setScanning(false);
        }
    }, [authHeader, scanDays]);

    const notifyOwners = useCallback(async () => {
        setNotifying(true);
        setWfError('');
        setWfNotice('');
        setNotifyResults([]);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/sms/leadnotes/notify', {
                method: 'POST',
                headers,
                body: JSON.stringify({ candidates }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            const results = Array.isArray(data.results) ? data.results : [];
            const okCount = typeof data.sent === 'number' ? data.sent : results.filter((r) => r.ok).length;
            const failed = results.filter((r) => !r.ok);
            setNotifyResults(failed);
            const rl = data.rateLimited ? ` ${data.rateLimited} skipped by the send quota.` : '';
            if (okCount > 0) {
                const tail = failed.length ? ` ${failed.length} not sent (see below).` : '';
                setWfNotice(`Texted ${okCount} owner${okCount === 1 ? '' : 's'}.${tail}${rl} Reply to any of them (or Simulate below) to see it append to Lead Notes.`);
            } else {
                setWfError(`No owners were texted — ${failed.length} not sent (see below).${rl}`);
            }
        } catch (e) {
            setWfError(e.message);
        } finally {
            setNotifying(false);
        }
    }, [authHeader, candidates]);

    const sender = status?.sender || {};
    const senderText = sender.mode === 'messaging_service'
        ? `Messaging Service ${sender.messagingServiceSid}`
        : sender.mode === 'from_number'
            ? `From ${sender.from}`
            : '— not set —';

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>💬 SMS</h2>
                {!embedded && <AdminMenu />}
            </div>
            <p style={styles.subtitle}>
                Send text messages and receive replies through Lyric&apos;s shared provider
                connection (<strong>Twilio</strong>). Everything is brokered server-side — no
                messaging credentials reach the browser. Configure credentials in
                <strong> AWS Secrets Manager</strong> (see <code>docs/sms-setup.md</code>).
            </p>

            {/* ── Connection status ─────────────────────────────────────────── */}
            <section style={styles.section}>
                <div style={styles.sectionHead}>
                    <h3 style={styles.h3}>Connection</h3>
                    <button type="button" onClick={loadStatus} style={styles.btn} disabled={loadingStatus}>
                        {loadingStatus ? 'Checking…' : '↻ Refresh'}
                    </button>
                </div>

                {statusError && <div style={styles.errorBox}>{statusError}</div>}

                {status && (
                    <table style={styles.kvTable}>
                        <tbody>
                            <tr>
                                <td style={styles.kvKey}>Status</td>
                                <td style={styles.kvVal}>
                                    {status.configured
                                        ? <span style={styles.ok}>● Configured — ready to send</span>
                                        : <span style={styles.bad}>● Not configured</span>}
                                </td>
                            </tr>
                            <tr>
                                <td style={styles.kvKey}>Provider</td>
                                <td style={styles.kvVal}>{status.provider || '—'}</td>
                            </tr>
                            <tr>
                                <td style={styles.kvKey}>Account</td>
                                <td style={styles.kvVal}>{status.accountSid || '—'}</td>
                            </tr>
                            <tr>
                                <td style={styles.kvKey}>Sender</td>
                                <td style={styles.kvVal}>{senderText}</td>
                            </tr>
                            <tr>
                                <td style={styles.kvKey}>Public base URL</td>
                                <td style={styles.kvVal}>{status.publicBaseUrl || '(derived from request headers)'}</td>
                            </tr>
                        </tbody>
                    </table>
                )}

                {status && !status.configured && (
                    <p style={styles.help}>
                        Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code> and either
                        <code> TWILIO_MESSAGING_SERVICE_SID</code> or <code>TWILIO_FROM_NUMBER</code> in
                        AWS Secrets Manager, then redeploy.
                    </p>
                )}
            </section>

            {/* ── Send a test message ───────────────────────────────────────── */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Send a test message</h3>
                <p style={styles.help}>
                    On a Twilio <em>trial</em> account the destination number must be verified in the
                    Twilio console first.
                </p>
                <div style={styles.formRow}>
                    <label style={styles.label}>
                        To (E.164)
                        <input
                            type="text"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="+15555550123"
                            style={styles.input}
                        />
                        {to.trim() && !toValid && (
                            <span style={styles.inlineBad}>Must be E.164, e.g. +15555550123</span>
                        )}
                    </label>
                </div>
                <div style={styles.formRow}>
                    <label style={styles.label}>
                        Message
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Lyric SMS test"
                            rows={3}
                            style={{ ...styles.input, resize: 'vertical' }}
                        />
                        <span style={styles.charCount}>{body.length} chars</span>
                    </label>
                </div>
                <button
                    type="button"
                    onClick={sendTest}
                    disabled={!canSend}
                    style={canSend ? styles.btnPrimary : styles.btnPrimaryDisabled}
                    title={status && !status.configured ? 'SMS is not configured yet' : ''}
                >
                    {sending ? 'Sending…' : 'Send SMS'}
                </button>

                {sendResult && (
                    <div style={styles.okBox}>
                        Sent to <strong>{sendResult.to}</strong>
                        {sendResult.sid ? <> · SID <code>{sendResult.sid}</code></> : null}
                        {sendResult.status ? <> · status <code>{sendResult.status}</code></> : null}
                    </div>
                )}
                {sendError && <div style={styles.errorBox}>{sendError}</div>}
            </section>

            {/* ── Salesforce: stale Lead Notes workflow ─────────────────────── */}
            <section style={styles.section}>
                <div style={styles.sectionHead}>
                    <h3 style={styles.h3}>Salesforce · stale Lead Notes</h3>
                    {sfSettings && (
                        <span style={sfSettings.configured ? styles.ok : styles.bad}>
                            {sfSettings.configured ? '● Connected' : '● Not configured'}
                        </span>
                    )}
                </div>
                <p style={styles.help}>
                    The end-to-end workflow: find Opportunities whose notes have gone stale, text
                    their owner for an update, and append the reply straight back to{' '}
                    <code>{sfSettings ? `${sfSettings.object}.${sfSettings.notesField}` : 'Lead Notes'}</code>.
                    Replies land via the webhook below (or <strong>Simulate</strong>) and write back live.
                </p>

                <div style={styles.scanRow}>
                    <label style={styles.inlineLabel}>
                        Stale for ≥
                        <input
                            type="number"
                            min={1}
                            value={scanDays}
                            onChange={(e) => setScanDays(e.target.value)}
                            style={styles.daysInput}
                        />
                        days
                    </label>
                    <button type="button" onClick={scanStale} disabled={scanning} style={styles.btnPrimary}>
                        {scanning ? 'Scanning…' : 'Scan stale notes'}
                    </button>
                    {candidates.length > 0 && (
                        <button type="button" onClick={notifyOwners} disabled={notifying} style={styles.btn}>
                            {notifying ? 'Texting…' : `Text ${candidates.length} owner${candidates.length === 1 ? '' : 's'}`}
                        </button>
                    )}
                </div>

                {wfNotice && <div style={styles.okBox}>{wfNotice}</div>}
                {wfError && <div style={styles.errorBox}>{wfError}</div>}

                {notifyResults.length > 0 && (
                    <div style={styles.notifyFailBox}>
                        <strong>Not sent ({notifyResults.length}):</strong>
                        <ul style={styles.notifyFailList}>
                            {notifyResults.map((r, i) => (
                                <li key={`${r.opportunityId || 'n'}-${i}`}>
                                    <code>{r.opportunityId || '—'}</code>
                                    {r.to ? <> · {r.to}</> : null}
                                    {' — '}
                                    {r.skipped ? 'skipped: ' : ''}{r.error || 'unknown error'}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {candidates.length > 0 && (
                    <table style={styles.kvTable}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Opportunity</th>
                                <th style={styles.th}>Owner</th>
                                <th style={styles.th}>Phone</th>
                                <th style={styles.th}>Last updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidates.map((c) => (
                                <tr key={c.opportunityId}>
                                    <td style={styles.kvVal}>{c.name || c.opportunityId}</td>
                                    <td style={styles.kvVal}>{c.ownerName || '—'}</td>
                                    <td style={styles.kvVal}>{c.ownerPhone || '—'}</td>
                                    <td style={styles.kvVal}>
                                        {c.lastModified ? new Date(c.lastModified).toLocaleDateString() : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* ── Inbound replies (live demo) ──────────────────────────────── */}
            <section style={styles.section}>
                <div style={styles.sectionHead}>
                    <h3 style={styles.h3}>Inbound replies (live)</h3>
                    <button type="button" onClick={loadRecent} style={styles.btn}>↻ Refresh</button>
                </div>
                <p style={styles.help}>
                    Replies received at the inbound webhook land here (newest first). While US A2P
                    10DLC registration is pending — which blocks real carrier delivery and inbound —
                    use <strong>Simulate a reply</strong> below to demo the full two-way flow with no
                    carrier dependency. Simulated replies run the same parse + capture path a real
                    signed webhook does.
                </p>

                <div style={styles.formRow}>
                    <label style={styles.label}>
                        From (the rep, E.164)
                        <input
                            type="text"
                            value={simFrom}
                            onChange={(e) => setSimFrom(e.target.value)}
                            placeholder="+15559876543"
                            style={styles.input}
                        />
                    </label>
                </div>
                <div style={styles.formRow}>
                    <label style={styles.label}>
                        Reply text
                        <textarea
                            value={simBody}
                            onChange={(e) => setSimBody(e.target.value)}
                            placeholder="Deal closed — PO signed today, kickoff next Monday."
                            rows={2}
                            style={{ ...styles.input, resize: 'vertical' }}
                        />
                    </label>
                </div>
                <div style={styles.formRow}>
                    <label style={styles.label}>
                        Opportunity Id <span style={styles.help}>(optional — append directly to this record; leave blank to use an open conversation from &ldquo;Text owners&rdquo;)</span>
                        <input
                            type="text"
                            value={simOpp}
                            onChange={(e) => setSimOpp(e.target.value)}
                            placeholder="006xxxxxxxxxxxxxxx"
                            style={styles.input}
                        />
                    </label>
                </div>
                <button
                    type="button"
                    onClick={simulateInbound}
                    disabled={!simValid}
                    style={simValid ? styles.btnPrimary : styles.btnPrimaryDisabled}
                >
                    {simulating ? 'Simulating…' : 'Simulate a reply'}
                </button>
                {simError && <div style={styles.errorBox}>{simError}</div>}
                {recentError && <div style={styles.errorBox}>{recentError}</div>}

                {recent.length === 0 ? (
                    <p style={styles.help}>No replies captured yet.</p>
                ) : (
                    <ul style={styles.replyList}>
                        {recent.map((m) => (
                            <li key={m.id} style={styles.replyItem}>
                                <div style={styles.replyMeta}>
                                    <strong style={styles.replyFrom}>{m.from || '—'}</strong>
                                    {m.simulated && <span style={styles.simBadge}>simulated</span>}
                                    <span style={styles.replyTime}>
                                        {m.receivedAt ? new Date(m.receivedAt).toLocaleString() : ''}
                                    </span>
                                </div>
                                <div style={styles.replyBody}>{m.body}</div>
                                {m.workflow && <WorkflowBadge wf={m.workflow} />}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* ── Inbound webhook ───────────────────────────────────────────── */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Inbound reply webhook</h3>
                <p style={styles.help}>
                    In the Twilio console, set the number&apos;s <strong>&ldquo;A message comes in&rdquo;</strong>
                    webhook (HTTP POST) to this URL. Lyric verifies every inbound request&apos;s
                    <code> X-Twilio-Signature</code> before processing it.
                </p>
                <div style={styles.webhookRow}>
                    <code style={styles.webhookUrl}>{webhookUrl}</code>
                    <button
                        type="button"
                        style={styles.btn}
                        onClick={() => { try { navigator.clipboard.writeText(webhookUrl); } catch { /* no-op */ } }}
                    >
                        Copy
                    </button>
                </div>
            </section>
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '900px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: '4px', lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: '4px', lineHeight: 1.5 },
    kvTable: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
    kvKey: { padding: '6px 8px', color: '#aaa', width: '160px', verticalAlign: 'top', fontSize: '0.85rem', borderBottom: '1px solid #232a31' },
    kvVal: { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #232a31' },
    ok: { color: '#4caf50', fontWeight: 600 },
    bad: { color: '#ef5350', fontWeight: 600 },
    formRow: { marginTop: '10px' },
    label: { display: 'flex', flexDirection: 'column', gap: '4px', color: '#ccc', fontSize: '0.85rem' },
    input: {
        padding: '8px 10px', borderRadius: '4px', border: '1px solid #2f3a44',
        background: '#161a1e', color: '#fff', width: '100%', boxSizing: 'border-box', fontSize: '13px',
    },
    charCount: { color: '#666', fontSize: '0.75rem', alignSelf: 'flex-end' },
    inlineBad: { color: '#ef5350', fontSize: '0.75rem' },
    btn: { padding: '8px 14px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    btnPrimary: { marginTop: '12px', padding: '8px 16px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnPrimaryDisabled: { marginTop: '12px', padding: '8px 16px', background: '#2a2a2a', color: '#666', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontWeight: 600 },
    errorBox: { marginTop: '10px', padding: '8px 12px', borderRadius: '4px', background: '#3a1f1f', border: '1px solid #633', color: '#ffb4b4', fontSize: '0.85rem' },
    okBox: { marginTop: '10px', padding: '8px 12px', borderRadius: '4px', background: '#1f3a24', border: '1px solid #365a3a', color: '#b4f0c0', fontSize: '0.85rem' },
    notifyFailBox: { marginTop: '10px', padding: '8px 12px', borderRadius: '4px', background: '#2f2719', border: '1px solid #5a4a36', color: '#ffd37a', fontSize: '0.82rem' },
    notifyFailList: { margin: '6px 0 0', paddingLeft: '18px' },
    webhookRow: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' },
    webhookUrl: { flex: 1, padding: '8px 10px', background: '#161a1e', border: '1px solid #2f3a44', borderRadius: '4px', color: '#9ad', fontSize: '13px', wordBreak: 'break-all' },
    replyList: { listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' },
    replyItem: { padding: '10px 12px', borderRadius: '6px', background: '#161a1e', border: '1px solid #2f3a44' },
    replyMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' },
    replyFrom: { color: '#4fc3f7', fontSize: '0.85rem' },
    simBadge: { fontSize: '0.7rem', color: '#ffd37a', background: '#3a331f', border: '1px solid #5a4f36', borderRadius: '10px', padding: '1px 8px' },
    replyTime: { color: '#666', fontSize: '0.75rem', marginLeft: 'auto' },
    replyBody: { color: '#ddd', fontSize: '0.9rem', lineHeight: 1.4, whiteSpace: 'pre-wrap' },
    th: { textAlign: 'left', padding: '6px 8px', color: '#8aa', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #2f3a44' },
    scanRow: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' },
    inlineLabel: { display: 'flex', alignItems: 'center', gap: '6px', color: '#ccc', fontSize: '0.85rem' },
    daysInput: { width: '64px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #2f3a44', background: '#161a1e', color: '#fff', fontSize: '13px' },
    wfOk: { marginTop: '6px', fontSize: '0.78rem', color: '#9be7ad' },
    wfWarn: { marginTop: '6px', fontSize: '0.78rem', color: '#ffd37a' },
    wfMuted: { marginTop: '6px', fontSize: '0.78rem', color: '#7a8690' },
};
